import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, ViewEncapsulation, ElementRef, Inject, OnInit, OnDestroy } from '@angular/core';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { DialogSuccessDoneComponent } from '../dialog-success-done/dialog-success-done.component';
import { DialogDoneConfirmation } from './dialog-done-confirmation.model';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Subscription } from 'rxjs/Subscription';
import { OrderSideEnum } from '@toro/common/shared/enum/orders/side.enum';
import { ComputerService } from '@toro/common/shared/services/computer/computer.service';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { CalculatingScenario } from '@toro/common/shared/enum/calculating-scenario/calculation-scenario.enum';
import { ExchangeActions } from '../../../../state-management/exchange/exchange.action';
import { ExchangeEffects } from '../../../../state-management/exchange/exchange.effects';
import { ApplicationMessagesAction } from '@toro/common/shared/state-management/application-messages/application-message.action';
import { Observable } from 'rxjs';
import { KeyStocks } from '@toro/common/shared/enum/key-stocks/key-stocks.enum';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { OrderOriginTypeEnum } from '@toro/common/shared/enum/orders/origin-type.enum';
import { Subject } from 'rxjs/Subject';
import { StocksMarketInteractionsService } from '../../services/stocks-market-interactions.service';
import { BillingEffects } from '../../../../state-management/billing/billing.effects';
import { BillingAction } from '../../../../state-management/billing/billing.action';
import { BrokeragePlatform } from '@toro/common/shared/enum/billing/brokerage-platform.enum';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { SuccessAsidePanelComponent } from "../../../../shared/panels/success-aside-panel/success-aside-panel.component";
import { BrokerageModel } from '@toro/common/shared/enum/billing/brokerage-model.enum';
import { debounceTime, filter, first, map } from 'rxjs/operators';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'dialog-done-confirmation',
    templateUrl: './dialog-done-confirmation.component.html',
    styleUrls: ['../common/dialog-right.component.scss', './dialog-done-confirmation.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DialogDoneConfirmationComponent extends DialogPanelComponentContent implements OnInit, OnDestroy {

    public originType = OrderOriginTypeEnum;
    public name: string;
    public stock: string;
    public type: string;
    public textPopover: string = '';
    public unityPrice: number;
    public currentPoints: number;
    public finalResult: number;
    public brokerageTax: number;
    public brokerageFactor: number;
    public currentPrice: number;
    public resultValue: number;
    public closingStrategy$: Observable<boolean>;
    public onClosing$ = new Subject<boolean>();
    public brokerageType: BrokerageModel;
    public traditionalProvisionedBrokerage: number;
    public isNaN = (value) => isNaN(value);
    public get isFuture(): boolean {
        return this.data.marketType === OrderMarketTypeEnum.Future;
    }

    public get provisionedBrokerage(): number {
        if (!this.brokerageFactor) return 0;

        const isTraditionalBrokerage = (this.brokerageType == BrokerageModel.Traditional) || (this.brokerageFactor && this.brokerageTax && this.data.originType != this.originType.Analyst);

        if (!isTraditionalBrokerage) {
            if (this.resultValue <= 0) return 0;
            let provisionedBrokerage = this.resultValue * 0.1;
            return provisionedBrokerage += provisionedBrokerage * 0.039;
        }
        return this.traditionalProvisionedBrokerage;
    }

    public get liquidProfit(): number {
        return this.resultValue - this.provisionedBrokerage;
    }

    public get actualResultTooltip(): string {
        return `<div>   
			<div class='pop-content'>
                <p>
                    <strong class='font-gray-color--darker'>O valor do ativo pode variar entre o momento da solicitação até o encerramento ser finalizado.</strong>
                </p>
			</div>
		</div>`;
    }

    public get provisionedBrokerageTooltip(): string {
        return `<div>   
			<div class='pop-content'>
                <p>
                    <strong class='font-gray-color--darker'>Este valor representa a soma dos valores de taxa de corretagem e a de emolumentos, cobrada pela própria bolsa de valores.</strong>
                </p>
			</div>
		</div>`;
    }

    private dolPtaxQuote: number;
    private disposableSubscriptions = new Array<Subscription>();

    constructor(
        private dialogReference: MatDialogRef<DialogDoneConfirmationComponent>,
        public element: ElementRef,
        @Inject(MAT_DIALOG_DATA) public data: DialogDoneConfirmation,
        private dialogsService: DialogsService,
        private store: Store<AppState>,
        private computerService: ComputerService,
        private exchangeEffects: ExchangeEffects,
        private interactions: StocksMarketInteractionsService,
        private billingEffects: BillingEffects,
    ) {
        super(element, true);

        this.textPopover = `<div>         
                <div class='pop-title'>Perde a corretagem Ganha-ganha</div>
                <div class='pop-content'>Em caso de prejuízo, você vai ser cobrado pela tabela tradicional. 
                    Em caso de lucro, a corretagem é 10% do lucro ou o valor da tabela, o que for maior.
                </div>
            </div>`;
    }

    public ngOnInit() {
        super.ngOnInit();
        // Cotação do instrumento
        this.disposableSubscriptions.push(
            this.store.select((state) => state.stocksExchange.stocksMarket.quote).pipe(
                filter((state) => state[this.data.symbol]),
                map((state) => state[this.data.symbol])
            ).subscribe((state) => {
                this.resultValue = this.calculate(state);
            }),
        );

        // Dados do instrumento
        this.disposableSubscriptions.push(
            this.store.select((state) => state.stocksExchange.stocksMarket.instruments).pipe(
                debounceTime(300),
                first((state) => !!state[this.data.symbol] && this.resultValue === undefined),
                map((state) => state[this.data.symbol])
            ).subscribe((state) => {
                this.resultValue = this.calculate(state);
            })
        );

        // Dados do billing
        this.disposableSubscriptions.push(
            this.billingEffects.getBrokerageTax$
                .subscribe((action) => {
                    if (action.payload.brokerageValue >= 0) {
                        this.brokerageTax = action.payload.brokerageValue;
                        this.traditionalProvisionedBrokerage = this.brokerageTax;
                        this.traditionalProvisionedBrokerage += this.traditionalProvisionedBrokerage * 0.039;
                    }
                }),
        );

        this.disposableSubscriptions.push(
            this.store.select((state) => state.billing)
                .pipe(
                    filter((billing) => !!billing && !!billing.brokerageModel)
                ).subscribe((billing: any) => {
                    this.brokerageType = billing.brokerageModel.type;
                })
        );

        if (this.data) {
            const obj = {
                isDaytrade: this.data.isDayTrade,
                orderQty: this.data.amount,
                platform: BrokeragePlatform.Hub,
                price: this.data.price,
                symbol: this.data.symbol,
            };
            this.store.dispatch({ type: BillingAction.getBrokerageTax, payload: obj });
        }

        // Taxa dolptax
        if (this.data.isPtax) {
            this.disposableSubscriptions.push(
                this.store.select((state) => state.stocksExchange.stocksMarket).pipe(
                    map((state) => state.quote[KeyStocks.DolPtax]),
                    filter((state) => state)
                ).subscribe((quote) => {
                    this.dolPtaxQuote = quote.PriceLastTrade || quote.OpeningPrice || quote.ClosingPricePreviousDay;
                }),
            );
            this.store.dispatch({ type: StocksMarketAction.getQuote, payload: KeyStocks.DolPtax });
        }

        if (this.data.brokerageFactor) {
            this.brokerageFactor = this.data.brokerageFactor;
        }

        this.store.dispatch({ type: StocksMarketAction.getQuote, payload: this.data.symbol });

        // Estratégia encerrada com sucesso
        this.disposableSubscriptions.push(
            this.exchangeEffects.closeStrategy$.pipe(
                filter((a) => a.type.equals(ExchangeActions.closeStrategySuccess))
            ).subscribe(() => {
                this.dispose();
                // Se o mercado estiver aberto
                if (this.data.marketOpen) {
                    const data = {
                        isScheduleOrder: false,
                        title: "Ordem de encerramento enviada com sucesso.",
                        content: "<p class='font-text--regular'>Você pode acompanhar esta ordem e conferir se este investimento foi encerrado em <strong>Meus investimentos.</strong></p>"
                    }
                    this.dialogsService.showComponentAsAsidePanel(SuccessAsidePanelComponent, true, data);
                } else {
                    // Se o Mercado estiver fechado e o cliente aceitar agendar o investimento
                    this.interactions.showClosingOrderBookedReceipt(this.data.marketoOpeningTime);
                }
            }),
        );

        // Erro ao encerrrar a estratégia
        this.disposableSubscriptions.push(
            this.exchangeEffects.closeStrategy$
                .subscribe((action) => {
                    if (action.type.equals(ExchangeActions.closeStrategyError)) {
                        this.store.dispatch({ type: ApplicationMessagesAction.genericError, payload: action.payload });
                        this.onClosing$.next(false);
                    }
                    this.dispose();
                }),
        );
        // Stream
        this.closingStrategy$ = this.store.select((state) => state.exchange.closingStrategyPending);
    }

    public calculate(quote) {
        this.currentPrice = (quote.PriceLastTrade || quote.priceLastTrade) ||
            (quote.OpeningPrice || quote.oppeningPrice) ||
            (quote.ClosingPricePreviousDay || quote.closingPricePreviousDay);

        return this.computerService.ComputeResultValue(
            this.data.amount,
            this.data.quoteFactor,
            this.currentPrice,
            this.data.price,
            this.data.marketType === OrderMarketTypeEnum.Future,
            this.data.side === OrderSideEnum.Compra,
            this.dolPtaxQuote,
        );
    }

    /**
     * handler para submit do form
     */
    public onSubmit(event: Event) {
        event.preventDefault();

        this.onClosing$.next(true);
        this.store.dispatch({ type: ExchangeActions.closeStrategy, payload: { strategyId: this.data.orderId } });
        this.sendToTracking('Investiment Closed', {
            strategyId: this.data.orderId,
            dayTrade: this.data.isDayTrade,
            ticker: this.data.symbol,
            origin: this.data.originAction,
            typeClosed: 'Manual',
            gainGain: this.data.originType === this.originType.Analyst,
            resultValue: this.resultValue,
            typeResult: this.resultValue > 0 ? 'GAIN' : 'LOSS',
        });
    }

    public ngOnDestroy() {
        // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    }

    /** Finaliza o componente */
    public dispose() {
        super.close(this.dialogReference, null);
    }

    /**
     * Função que dispara evento pro segment
     *
     * @param typeAction título da action
     * @param eventsTracking objeto de eventos
     */
    private sendToTracking(typeAction, eventsTracking) {
        const payloadUser = { type: typeAction, events: eventsTracking };
        this.store.dispatch({ type: TrackingActions.trackEvents, payload: payloadUser });
    }

}
